import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmailStore } from '@/stores/useEmailStore';
import { Mail, CheckCircle2, Eye, MousePointerClick, AlertTriangle, TrendingUp } from 'lucide-react';

export function EmailDashboard() {
  const { stats, statsLoading, fetchStats } = useEmailStore();

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (statsLoading && !stats) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader>
            <CardContent><Skeleton className="h-8 w-16" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No email data yet</p>
          <p className="text-sm mt-1">Send your first email to see stats here.</p>
        </CardContent>
      </Card>
    );
  }

  const statCards = [
    {
      title: 'Emails Sent',
      value: stats.total_sent || 0,
      subtitle: `${stats.emails_last_30_days || 0} in last 30 days`,
      icon: Mail,
    },
    {
      title: 'Delivered',
      value: stats.total_delivered || 0,
      icon: CheckCircle2,
    },
    {
      title: 'Open Rate',
      value: `${stats.open_rate || 0}%`,
      subtitle: `${stats.total_opened || 0} opened`,
      icon: Eye,
    },
    {
      title: 'Clicked',
      value: stats.total_clicked || 0,
      subtitle: stats.total_bounced ? `${stats.total_bounced} bounced` : undefined,
      icon: MousePointerClick,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              {card.subtitle && (
                <p className="text-xs text-muted-foreground mt-1">{card.subtitle}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {stats.recent_campaigns && stats.recent_campaigns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_campaigns.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.sent_count || 0} sent &middot; {c.opened_count || 0} opened
                    </p>
                  </div>
                  <Badge
                    variant={
                      c.status === 'sent' ? 'default' :
                      c.status === 'draft' ? 'secondary' :
                      c.status === 'failed' ? 'destructive' : 'outline'
                    }
                  >
                    {c.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
